import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import * as authApi from "../api/auth";
import { setAccessToken } from "../api/client";
import { hidratarTudo, limparCaches } from "../services/dataCache";
import { listarEventos } from "../services/eventosStore";
import { sincronizarCoresTurma } from "../utils/coresTurma";

const AuthContext = createContext(null);

function lerUsuarioPersistido() {
  try {
    return JSON.parse(localStorage.getItem("usuario") || "null");
  } catch {
    return null;
  }
}

async function hidratarUsuario(me) {
  try {
    await hidratarTudo({ instituicaoId: me?.instituicaoId });
  } catch {}
  listarEventos().catch(() => {});
  // Espelha as cores das turmas do backend (sincronizadas entre dispositivos).
  sincronizarCoresTurma().catch(() => {});
}

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(lerUsuarioPersistido);
  const [inicializando, setInicializando] = useState(true);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const token = localStorage.getItem("token");
      const persistido = lerUsuarioPersistido();

      // Sem indicação de sessão prévia (nem token nem usuário persistido)
      // não chama /auth/refresh nem /usuarios/me — evita 401 ruidoso no
      // primeiro load sem login.
      if (!token && !persistido) {
        if (ativo) setInicializando(false);
        return;
      }

      try {
        if (!token) {
          await authApi.refresh();
        }
        const me = await authApi.getUsuarioAtual();
        if (!ativo) return;
        setUsuario(me);
        localStorage.setItem("usuario", JSON.stringify(me));
        await hidratarUsuario(me);
      } catch {
        if (!ativo) return;
        setUsuario(null);
        localStorage.removeItem("usuario");
        setAccessToken(null);
      } finally {
        if (ativo) setInicializando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async ({ email, senha }) => {
    await authApi.login({ email, senha });
    const me = await authApi.getUsuarioAtual();
    setUsuario(me);
    localStorage.setItem("usuario", JSON.stringify(me));
    await hidratarUsuario(me);
    return me;
  }, []);

  const sair = useCallback(async () => {
    setUsuario(null);
    limparCaches();
    await authApi.logout();
  }, []);

  const recarregarUsuario = useCallback(async () => {
    try {
      const me = await authApi.getUsuarioAtual();
      setUsuario(me);
      localStorage.setItem("usuario", JSON.stringify(me));
      await hidratarUsuario(me);
      return me;
    } catch (err) {
      console.error("Erro ao recarregar usuário:", err);
      throw err;
    }
  }, []);

  useEffect(() => {
    const handle = () => {
      setUsuario(null);
      limparCaches();
    };
    window.addEventListener("auth:sessao-expirada", handle);
    return () => window.removeEventListener("auth:sessao-expirada", handle);
  }, []);

  // Atualização automática global (estratégia "foco/visibilidade"): ao voltar
  // para a aba ou focar a janela, recarrega o usuário (/usuarios/me → vínculos,
  // papel, turmas) e avisa as páginas para refazerem seus fetches. Resolve o
  // "preciso dar F5": evento publicado, aluno add/removido, nome de turma,
  // responsável removido que ainda editava — tudo reflete sem refresh manual.
  useEffect(() => {
    let atualizando = false;
    const atualizar = async () => {
      if (atualizando) return;
      if (!localStorage.getItem("usuario") && !localStorage.getItem("token"))
        return;
      atualizando = true;
      try {
        await recarregarUsuario();
      } catch {
        // silencioso: sessão pode ter expirado; o fluxo normal trata o 401
      } finally {
        atualizando = false;
        // Páginas já escutam estes eventos para refazer seus próprios fetches.
        window.dispatchEvent(new Event("turmas:changed"));
        window.dispatchEvent(new Event("app:data-changed"));
      }
    };

    const onFocus = () => atualizar();
    const onVisibility = () => {
      if (document.visibilityState === "visible") atualizar();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [recarregarUsuario]);

  // Heartbeat: checa a sessão a cada 20s para deslogar na hora quando o backend
  // revoga o acesso (instituição desativada/excluída, usuário removido) mesmo com
  // o usuário parado na tela. Um 401 dispara, via client.js, o refresh; quando o
  // refresh também falha (conta inativa), o evento "auth:sessao-expirada" é
  // emitido e o usuário cai na tela de login automaticamente.
  useEffect(() => {
    const POLL_MS = 20000;
    const id = setInterval(() => {
      if (!localStorage.getItem("usuario") && !localStorage.getItem("token"))
        return;
      authApi.getUsuarioAtual().catch(() => {});
    }, POLL_MS);
    return () => clearInterval(id);
  }, []);

  const isAdmin =
    String(usuario?.tipo ?? "").toLowerCase() === "admin" ||
    usuario?.tipo === 4;

  return (
    <AuthContext.Provider
      value={{
        usuario,
        inicializando,
        entrar,
        sair,
        isAdmin,
        recarregarUsuario,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  return ctx;
}
